import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import {
  type FC,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { IconButton } from '@/components/ui/IconButton';
import { XStack } from '@/components/ui/layout';
import { isAllowedImageUrl } from '@/components/ui/smoothdown/img-sanitizer';
import { cn } from '@/lib/utils';

export const ImageNodeView: FC<NodeViewProps> = ({
  node,
  selected,
  updateAttributes,
  deleteNode,
  editor,
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [resizing, setResizing] = useState(false);
  const moveHandlerRef = useRef<((event: PointerEvent) => void) | null>(null);
  const upHandlerRef = useRef<(() => void) | null>(null);
  const cancelHandlerRef = useRef<(() => void) | null>(null);
  const blurHandlerRef = useRef<(() => void) | null>(null);
  const captureTargetRef = useRef<HTMLElement | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const startSizeRef = useRef<{ width: number; height: number } | null>(null);

  const cleanupListeners = useCallback(() => {
    if (moveHandlerRef.current) {
      window.removeEventListener('pointermove', moveHandlerRef.current);
      moveHandlerRef.current = null;
    }
    if (upHandlerRef.current) {
      window.removeEventListener('pointerup', upHandlerRef.current);
      upHandlerRef.current = null;
    }
    if (cancelHandlerRef.current) {
      window.removeEventListener('pointercancel', cancelHandlerRef.current);
      cancelHandlerRef.current = null;
    }
    if (blurHandlerRef.current) {
      window.removeEventListener('blur', blurHandlerRef.current);
      blurHandlerRef.current = null;
    }
    if (captureTargetRef.current && pointerIdRef.current !== null) {
      captureTargetRef.current.releasePointerCapture?.(pointerIdRef.current);
    }
    captureTargetRef.current = null;
    pointerIdRef.current = null;
  }, []);

  useEffect(() => cleanupListeners, [cleanupListeners]);

  const handleDownload = useCallback(() => {
    const src = node.attrs.src as string | undefined;
    if (!src || !isAllowedImageUrl(src)) return;
    const fileId = node.attrs.fileId as string | undefined;
    const filename =
      (node.attrs.alt as string) || `image-${fileId || 'download'}.png`;

    const anchor = document.createElement('a');
    anchor.href = src;
    anchor.download = filename;
    anchor.click();
  }, [node.attrs.src, node.attrs.alt, node.attrs.fileId]);

  const handleResize = useCallback(
    (side: 'w' | 'e', e: ReactPointerEvent) => {
      if (!editor.isEditable) return;
      e.preventDefault();
      e.stopPropagation();
      const img = imgRef.current;
      if (!img) return;

      setResizing(true);
      const startX = e.clientX;
      const startWidth = img.offsetWidth;
      const startHeight = img.offsetHeight;
      startSizeRef.current = { width: startWidth, height: startHeight };
      const attrWidth =
        typeof node.attrs.width === 'number'
          ? node.attrs.width
          : Number(node.attrs.width);
      const attrHeight =
        typeof node.attrs.height === 'number'
          ? node.attrs.height
          : Number(node.attrs.height);
      const ratio =
        img.naturalWidth > 0
          ? img.naturalHeight / img.naturalWidth
          : attrWidth > 0 && attrHeight > 0
            ? attrHeight / attrWidth
            : startWidth > 0 && startHeight > 0
              ? startHeight / startWidth
              : 1;

      cleanupListeners();

      const pointerId = e.pointerId;
      pointerIdRef.current = pointerId;
      if (e.currentTarget instanceof HTMLElement) {
        captureTargetRef.current = e.currentTarget;
        e.currentTarget.setPointerCapture(pointerId);
      }

      const onMove = (ev: PointerEvent) => {
        if (
          pointerIdRef.current !== null &&
          ev.pointerId !== pointerIdRef.current
        ) {
          return;
        }
        const delta = side === 'e' ? ev.clientX - startX : startX - ev.clientX;
        const w = Math.max(100, startWidth + delta);
        img.style.width = `${w}px`;
        img.style.height = `${Math.round(w * ratio)}px`;
      };

      const onUp = () => {
        cleanupListeners();
        setResizing(false);
        updateAttributes({
          width: img.offsetWidth,
          height: img.offsetHeight,
        });
        startSizeRef.current = null;
      };

      const onCancel = () => {
        cleanupListeners();
        setResizing(false);
        const startSize = startSizeRef.current;
        if (startSize && imgRef.current) {
          imgRef.current.style.width = `${startSize.width}px`;
          imgRef.current.style.height = `${startSize.height}px`;
        }
        startSizeRef.current = null;
      };

      const onBlur = () => {
        cleanupListeners();
        setResizing(false);
        const startSize = startSizeRef.current;
        if (startSize && imgRef.current) {
          imgRef.current.style.width = `${startSize.width}px`;
          imgRef.current.style.height = `${startSize.height}px`;
        }
        startSizeRef.current = null;
      };

      moveHandlerRef.current = onMove;
      upHandlerRef.current = onUp;
      cancelHandlerRef.current = onCancel;
      blurHandlerRef.current = onBlur;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
      window.addEventListener('blur', onBlur);
    },
    [
      cleanupListeners,
      editor.isEditable,
      node.attrs.height,
      node.attrs.width,
      updateAttributes,
    ]
  );

  const src = node.attrs.src as string | undefined;
  if (!src || !isAllowedImageUrl(src)) {
    return (
      <NodeViewWrapper className="lb-editor-image">
        {node.attrs.alt || null}
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      className={`lb-editor-image group${resizing ? ' resizing' : ''}${selected ? ' selected' : ''}`}
    >
      <img
        ref={imgRef}
        src={src}
        alt={node.attrs.alt || ''}
        title={node.attrs.title || undefined}
        style={{
          cursor: 'default',
          ...(node.attrs.width && { width: `${node.attrs.width}px` }),
          ...(node.attrs.height && { height: `${node.attrs.height}px` }),
        }}
        data-drag-handle={editor.isEditable || undefined}
      />
      <XStack
        className={cn(
          'lb-editor-image-actions',
          'cursor-default',
          'absolute right-4 top-2.5 z-10',
          'opacity-0 group-hover:opacity-100 has-[:focus-visible]:opacity-100 group-has-[:focus-visible]:opacity-100 transition-opacity',
          'items-center gap-1',
          'w-fit',
          'rounded-lg smoothing-default',
          'border border-border-strong',
          'bg-surface',
          'px-1 py-1',
          'shadow-xl'
        )}
      >
        <IconButton
          icon={Icons.download}
          size="xs"
          variant="ghost"
          tooltip="Download"
          onClick={handleDownload}
          tooltipOptions={{ sideOffset: 9 }}
        />
        {editor.isEditable && (
          <IconButton
            icon={Icons.trash}
            size="xs"
            variant="ghost"
            tooltip="Delete"
            tooltipOptions={{ hotkey: ['delete'], sideOffset: 9 }}
            onClick={deleteNode}
            className="hover:text-red-500"
          />
        )}
      </XStack>
      {editor.isEditable && (
        <div
          className="lb-editor-image-resize-handle left"
          onPointerDown={(e) => handleResize('w', e)}
        />
      )}
      {editor.isEditable && (
        <div
          className="lb-editor-image-resize-handle right"
          onPointerDown={(e) => handleResize('e', e)}
        />
      )}
    </NodeViewWrapper>
  );
};
